<template>
  <div>
    <v-card v-if="user.stripeAccountExists && !stripeLoading && !(numCustomers > maxCustomers)">
      <v-card-title>
        There was a problem with your account, please update your billing information.
      </v-card-title>
      <v-card-text>
        <p v-if="errorMessage">
          {{ errorMessage }}
        </p>
        <v-btn v-if="!errorMessage" @click="toCustomerPortal">
          Manage Billing
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card v-if="!user.stripeAccountExists && !stripeLoading || user.stripeAccountExists && numCustomers > maxCustomers && !stripeLoading" >
      <v-card-title>
        Get Started with CertainFee
      </v-card-title>
      <v-card-text>
        <p class="mb-2 font-weight-medium">Choose a plan</p>
        <v-radio-group
          v-model="selected"
          column
        >
          <v-radio
            v-for="(plan, index) in plans"
            :disabled="plan.maxCustomers < numCustomers"
            :label="plan.title"
            :key="plan.name"
            :value="index"
          />
        </v-radio-group>

        <p class="mb-2 font-weight-medium">Billing interval</p>
        <v-radio-group
          v-model="billingInterval"
          row
        >
          <v-radio
            label="Monthly"
            value="month"
          />
          <v-radio
            label="Annual"
            value="year"
          />
        </v-radio-group>

        <p v-if="selectedPlan" class="mb-4">
          {{ selectedPlan.name }} — {{ formattedSelectedPrice }}
        </p>

        <p v-if="errorMessage">
          {{ errorMessage }}
        </p>
        <v-btn v-if="!errorMessage && canCheckout" @click="createStripeSession">
          Set Up Billing
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card class="text-center pa-5" v-if="stripeLoading">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </v-card>
    <br />

  </div>
</template>

<script>
import { DB } from '../firebase/db'
import { Functions } from '../firebase/functions'
import { mapState } from 'vuex'
import { STRIPE_PLANS, getPriceIdForPlan } from '../stripePlans'

const createCheckoutSession = Functions.httpsCallable('createCheckoutSession')
const accessCustomerPortal = Functions.httpsCallable('accessCustomerPortal')
const recreateStripeCustomer = Functions.httpsCallable('recreateStripeCustomer')

export default {
  props: ['numCustomers'],
  data() {
    return {
      customer: '',
      customer_email: '',
      mode: 'subsc',
      sessionId: '',
      errorMessage: null,
      stripe: '',
      selected: undefined,
      billingInterval: 'month',
      plans: STRIPE_PLANS,
      successUrl: 'https://certainfee.com/',
      cancelUrl: 'https://certainfee.com/',
    }
  },
  computed: {
    ...mapState(['user', 'stripeLoading', 'maxCustomers']),
    uid: function() {
      return this.$store.state.user.uid
    },

    email: function() {
      return this.$store.state.user.email
    },

    selectedPlan() {
      if (this.selected === undefined || this.selected === null) {
        return null
      }
      return this.plans[this.selected]
    },

    formattedSelectedPrice() {
      if (!this.selectedPlan) {
        return ''
      }
      if (this.billingInterval === 'year') {
        return `$${this.selectedPlan.annualAmount}/year`
      }
      return `$${this.selectedPlan.monthlyAmount}/month`
    },

    canCheckout() {
      return this.selectedPlan && this.selectedPlan.maxCustomers >= this.numCustomers
    },
  },
  methods: {
    async createStripeSession() {
      if (!this.selectedPlan) {
        return
      }

      this.customer = await DB.collection('user')
        .doc(this.uid)
        .collection('stripe')
        .doc('stripe_customer')
        .get()

      if(!this.customer.exists) {
        await recreateStripeCustomer({
          email: this.user.email,
          uid: this.user.uid,
        })

        this.customer = await DB.collection('user')
          .doc(this.uid)
          .collection('stripe')
          .doc('stripe_customer')
          .get()
      }

      const priceId = getPriceIdForPlan(this.selectedPlan, this.billingInterval)

      createCheckoutSession({
        priceId,
        successUrl: this.successUrl,
        cancelUrl: this.cancelUrl,
        customer: this.customer.data().stripe_customer_id
      })
      .then((response) => {
        if (!response.data || !response.data.sessionId) {
          this.errorMessage = 'Could not start checkout. Please try again or contact support.'
          return
        }
        this.sessionId = response.data.sessionId
        this.redirectToCheckout()
      })
      .catch((error) => {
        console.error('createCheckoutSession failed:', error)
        this.errorMessage = error.message || 'Could not start checkout. The billing plan may not be set up in Stripe yet.'
      })
    },

    configureStripe() {
      const key = process.env.VUE_APP_STRIPE_LIVE_Public_Key
      if (!key) {
        console.error('VUE_APP_STRIPE_LIVE_Public_Key is missing. Restart npm run serve or rebuild after updating .env.')
        this.errorMessage = 'Billing setup is unavailable. Please refresh after the app is redeployed.'
        return
      }
      this.stripe = window.Stripe(key)
    },

    redirectToCheckout() {
      if (!this.sessionId) {
        this.errorMessage = 'Checkout session is missing. Please try again.'
        return
      }
      this.stripe
        .redirectToCheckout({
          sessionId: this.sessionId,
        })
        .then((result) => {
          if (result.error) {
            this.errorMessage = result.error.message
          }
        })
        .catch(err => console.log(err))
    },

    async toCustomerPortal() {
      if(!this.customer) {
        this.customer = await DB.collection('user')
          .doc(this.uid)
          .collection('stripe')
          .doc('stripe_customer')
          .get()
      }
      this.customer.data().stripe_customer_id
      accessCustomerPortal({
        customer: this.customer.data().stripe_customer_id,
        returnUrl: this.successUrl,
      }).then((res) => {
        location.href = res.data.url
      })
    }
  },

  mounted() {
    this.configureStripe()
  },
}
</script>

<style scoped></style>
